import { useEffect, useState } from "react";
import { BarChart3, TrendingDown, FileSpreadsheet } from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import api from "../lib/api";
import { fmtCurrency } from "../lib/format";
import { exportXeroInvoices } from "../lib/integrations";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";

const COLORS = ["#0F172A", "#2563EB", "#60A5FA", "#94A3B8", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444"];

export default function Reports() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports/summary").then((res) => setR(res.data)); }, []);
  if (!r) return <div className="text-slate-400">Loading reports…</div>;

  const exportXero = async () => {
    const { data } = await api.get("/opportunities");
    const paid = data.filter((o) => o.stage === "Won" && o.invoice_paid);
    if (!paid.length) { toast.error("No paid invoices to export"); return; }
    exportXeroInvoices(paid);
    toast.success(`Exported ${paid.length} invoices for Xero`);
  };

  return (
    <div>
      <PageHeader title="Reports & Analytics" subtitle="Revenue, territory and discount analysis" icon={BarChart3}>
        <Button variant="outline" size="sm" onClick={exportXero} data-testid="export-xero-btn">
          <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export for Xero
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Revenue by Brand">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={r.by_brand}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 10, fontSize: 13 }} />
              <Bar dataKey="value" fill="#2563EB" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Revenue by Territory">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={r.by_region} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={(e) => e.name}>
                {r.by_region.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 10, fontSize: 13 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Revenue by BDM">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={r.by_bdm} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#64748B" }} width={110} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 10, fontSize: 13 }} />
              <Bar dataKey="value" fill="#0F172A" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Margin Protection · Discount Analysis">
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-4 flex items-center gap-3">
            <TrendingDown className="h-8 w-8 text-red-500" />
            <div>
              <div className="text-xs text-red-500 uppercase tracking-wider font-semibold">Revenue Lost to Discounting</div>
              <div className="font-display text-2xl font-bold text-red-600">{fmtCurrency(r.revenue_lost_to_discount)}</div>
            </div>
          </div>
          <h4 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Average Discount by BDM</h4>
          <div className="space-y-2">
            {r.avg_discount_by_bdm.map((b) => (
              <div key={b.bdm} className="flex items-center gap-3">
                <span className="text-sm text-slate-600 w-32 truncate">{b.bdm}</span>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: `${Math.min(b.avg_discount * 2.5, 100)}%` }} />
                </div>
                <span className="text-sm font-semibold text-navy-900 w-12 text-right">{b.avg_discount}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h3 className="font-display font-semibold text-navy-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}
